import { gql } from '@apollo/client/core';
import { executeQuery, executeMutation } from '@/components/commons/apolloUtils';
import LocalStorage from '@/components/commons/LocalStorage';
import { refreshApolloClient } from '@/components/commons/apollo';

const KEY_ARTICLE_USER = 'article-user';
const KEY_ARTICLE_CART = 'article-cart';
const KEY_ARTICLE_TOKEN = 'article-token';

export default class UserService {
  static #user = null;
  static #cart = null;
  static #customerToken = null;

  static setUser(user) {
    if (user) {
      try {
        LocalStorage.set(KEY_ARTICLE_USER, JSON.stringify(user));
      } catch (error) {}

      UserService.setCart(user.cart);
    } else {
      try {
        LocalStorage.remove(KEY_ARTICLE_USER);
      } catch (error) {}
    }

    UserService.#user = user;

    refreshApolloClient({
      restoreCache: true,
      ssr: false,
      sessionid: UserService.getSessionId(),
      customertoken: UserService.getCustomerToken(),
    });
  }

  static setCart(cart) {
    if (cart) {
      try {
        LocalStorage.set(KEY_ARTICLE_CART, JSON.stringify(cart));
      } catch (error) {}
    } else {
      try {
        LocalStorage.remove(KEY_ARTICLE_CART);
      } catch (error) {}
    }

    UserService.#cart = cart;
  }

  static getUser() {
    if (UserService.#user) {
      return UserService.#user;
    }

    UserService.refreshUser();
    return UserService.#user;
  }

  static refreshUser() {
    try {
      UserService.#user = JSON.parse(LocalStorage.get(KEY_ARTICLE_USER));
      UserService.setCart(UserService.#user.cart);

      refreshApolloClient({
        restoreCache: true,
        ssr: false,
        sessionid: UserService.getSessionId(),
        customertoken: UserService.getCustomerToken(),
      });
    } catch (error) {
      UserService.#user = null;
      UserService.setCart(null);
    }
  }

  static getCart() {
    if (UserService.#cart) {
      return UserService.#cart;
    }

    UserService.refreshCart();
    return UserService.#cart;
  }

  static refreshCart() {
    try {
      UserService.#cart = JSON.parse(LocalStorage.get(KEY_ARTICLE_CART));
    } catch (error) {
      UserService.#cart = null;
    }
  }

  static setCustomerToken(customerToken) {
    if (customerToken) {
      try {
        LocalStorage.set(KEY_ARTICLE_TOKEN, customerToken);
      } catch (error) {}
    } else {
      try {
        LocalStorage.remove(KEY_ARTICLE_TOKEN);
      } catch (error) {}
    }
  }

  static getCustomerToken() {
    if (UserService.#customerToken) {
      return UserService.#customerToken;
    }
    UserService.refreshCustomerToken();
    return UserService.#customerToken;
  }

  static refreshCustomerToken() {
    try {
      UserService.#customerToken = LocalStorage.get(KEY_ARTICLE_TOKEN);
    } catch (error) {
      UserService.#customerToken = null;
    }
  }

  static getEmail() {
    return UserService.getUser()?.email;
  }

  static getSessionId() {
    return UserService.getUser()?.sessionId;
  }

  static isVerified() {
    return !!UserService.getUser()?.verified;
  }

  static isExpired() {
    return !!UserService.getUser()?.expired;
  }

  static isRegistered() {
    return !!UserService.getUser()?.registered;
  }

  static isTrade() {
    return !!UserService.getUser()?.isTrade;
  }

  static async login(account) {
    try {
      const { userLogin } = await executeMutation(
        gql`
          mutation USER_LOGIN($input: UserLoginInput!, $userAgent: String) {
            userLogin(input: $input, userAgent: $userAgent) {
              success
              errors
              step
              email
              customerToken
              loggedIn
              userInfo {
                email
                firstName
                lastName
                sessionId
                verified
                expired
                registered
                isTrade
              }
            }
          }
        `,
        {
          input: account,
          userAgent: navigator.userAgent,
        },
        {
          errorPolicy: 'ignore',
        },
      );

      return userLogin;
    } catch (error) {
      console.error('userservice login error', error);
      window.location.reload();
      return {};
    }
  }

  static async validate(account) {
    const { email } = account;
    if (email) {
      const { userValidate } = await executeQuery(
        gql`
          query USER_VALIDATE($email: String!) {
            userValidate(email: $email) {
              success
              step
              errors
              email
            }
          }
        `,
        {
          email,
        },
      );

      return userValidate;
    }

    return null;
  }

  static async register(account) {
    const { userRegister, success, errors } = await executeMutation(
      gql`
        mutation USER_REGISTER($input: UserRegisterInput!, $userAgent: String) {
          userRegister(input: $input, userAgent: $userAgent) {
            success
            errors
            step
            email
            customerToken
            userInfo {
              email
              firstName
              lastName
              sessionId
              verified
              expired
              registered
              isTrade
            }
          }
        }
      `,
      {
        input: account,
        userAgent: navigator.userAgent,
      },
      null,
      'validation'
    );

    return {success, errors, ...userRegister};
  }

  static async forgotPassword(account) {
    const { userForgotPassword } = await executeMutation(
      gql`
        mutation USER_FORGOT_PASSWORD($input: UserForgotPasswordInput!) {
          userForgotPassword(input: $input) {
            success
            errors
          }
        }
      `,
      {
        input: account,
      },
    );

    return userForgotPassword;
  }

  static async reset({ account, isSuspiciousLogin, emailEventId }) {
    const { userReset, success, errors } = await executeMutation(
      gql`
        mutation USER_RESET($input: UserResetInput!, $userAgent: String) {
          userReset(input: $input, userAgent: $userAgent) {
            success
            errors
            customerToken
          }
        }
      `,
      {
        input: {
          ...account,
          isSuspiciousLogin,
          emailEventId,
        },
        userAgent: navigator.userAgent,
      },
      null,
      'validation'
    );

    return {success, errors, ...userReset};
  }

  static async checkResetCode({ cypher, code }) {
    const { userCheckResetCode } = await executeQuery(
      gql`
        query USER_CHECK_RESET_CODE($cypher: String, $code: String) {
          userCheckResetCode(cypher: $cypher, code: $code) {
            success
            errors
          }
        }
      `,
      {
        cypher,
        code,
      },
    );

    return userCheckResetCode;
  }

  static async verify({ cypher, code }) {
    const { userVerify } = await executeMutation(
      gql`
        mutation USER_VERIFY($input: UserVerifyInput!) {
          userVerify(input: $input) {
            success
            verifySuccess
            customerSession
            customerToken
            toRegisterUser {
              email
              code
              from
            }
          }
        }
      `,
      {
        input: {
          cypher,
          code,
        },
      },
    );

    return userVerify;
  }

  static async verifyLoginFromEmail({ cypher, code }) {
    const { userVerifyLoginFromEmail } = await executeQuery(
      gql`
        query VERIFY_USER_LOGIN_FROM_EMAIL($cypher: String, $code: String) {
          userVerifyLoginFromEmail(cypher: $cypher, code: $code) {
            success
            showSocial
            canLogin
            email
          }
        }
      `,
      {
        cypher,
        code,
      },
    );

    return userVerifyLoginFromEmail;
  }
}
